import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Modal, Field } from "../../components/ui-modal";
import { PhaseSelector } from "../../components/PhaseSelector";
import { PageTitle } from "../../components/Layout";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";

export default function WorkflowTemplates() {
  const { confirm } = useConfirm();
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, name, phases}
  const load = () => api.get("/admin/workflow-templates").then((r) => setTemplates(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ name: "", phases: [] });
  const openEdit = (t) => setEditing({ id: t.id, name: t.name, phases: t.phases.map((p) => ({ ...p })) });
  const save = async (e) => {
    e.preventDefault();
    if (!editing.name.trim()) return toast.error("Nom requis");
    if (editing.phases.length === 0) return toast.error("Sélectionnez au moins une phase");
    const body = { name: editing.name.trim(), phases: editing.phases };
    if (editing.id) await api.put(`/admin/workflow-templates/${editing.id}`, body);
    else await api.post("/admin/workflow-templates", body);
    toast.success("Modèle enregistré"); setEditing(null); load();
  };
  const del = async (t) => {
    if (!(await confirm({ title: `Supprimer le modèle « ${t.name} » ?`, confirmLabel: "Supprimer", tone: "danger" }))) return;
    await api.delete(`/admin/workflow-templates/${t.id}`); toast.success("Modèle supprimé"); load();
  };

  return (
    <div>
      <PageTitle overline="Automatisations" title="Workflows des missions"
        action={<button onClick={openNew} data-testid="new-template-btn" className="bg-bordeaux text-white rounded-sm px-4 py-2 text-sm hover:bg-bordeaux-hover flex items-center gap-1.5"><Icons.Plus size={16} /> Nouveau modèle</button>} />
      <p className="text-sm text-marine/80 mb-5">Créez et réutilisez des modèles de phases pour vos missions afin d'automatiser leur déroulé.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
        {templates.map((t) => (
          <div key={t.id} data-testid={`template-card-${t.id}`} className="bg-sable rounded-sm border border-marine/10 p-5">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="font-display text-lg text-marine">{t.name}</div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => openEdit(t)} data-testid={`template-edit-${t.id}`} aria-label={`Modifier ${t.name || "ce modèle"}`} title="Modifier" className="text-marine hover:bg-marine/10 rounded-sm p-1"><Icons.Pencil size={16} /></button>
                <button onClick={() => del(t)} data-testid={`template-delete-${t.id}`} className="text-rouge hover:bg-rouge/10 rounded-sm p-1"><Icons.Trash2 size={16} /></button>
              </div>
            </div>
            <div className="text-xs text-marine/80 mb-2">{t.phases.length} phase(s)</div>
            <div className="flex flex-wrap gap-1.5">
              {t.phases.map((p, i) => <span key={p.id} className="text-[9px] bg-marine-50 text-marine border border-marine/30 px-2 py-0.5 rounded-sm">{i + 1}. {p.label}</span>)}
            </div>
          </div>
        ))}
        {templates.length === 0 && <p className="text-sm text-marine/80">Aucun modèle. Créez-en un pour appliquer un workflow en un clic à vos missions.</p>}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Modifier le modèle" : "Nouveau modèle de workflow"}>
          <form onSubmit={save} className="space-y-4">
            <Field label="Nom du modèle"><input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="template-name" className="inp" placeholder="Ex. Montage vidéo, Rédaction SEO..." /></Field>
            <div className="border border-marine/15 rounded-sm p-3 bg-sable">
              <div className="text-xs uppercase tracking-wider text-bordeaux mb-2 font-medium">Phases du modèle</div>
              <PhaseSelector phases={editing.phases} setPhases={(ph) => setEditing({ ...editing, phases: ph })} showPlanning={false} showTemplates={false} showDescription={true} showToValidate={true} />
            </div>
            <div className="flex justify-end gap-2"><button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-marine/80">Annuler</button><button data-testid="template-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm">Enregistrer</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
